import { AnimatePresence, motion } from 'framer-motion'
import React, { CSSProperties, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { BaseModalType } from '@context/ModalsContext'

import { ButtonProps, IconButton } from '@ui/components/Button'
import { Modal } from '@ui/components/Modal'
import { CloseIcon } from '@ui/icons/Close.icon'

import { TickmillProductType } from '@model/TickmillProductType'

import { useTryProduct } from '@hooks/useTryProduct'

import { EASE_OUT } from '@shared/animations/consts'
import { Box } from '@shared/components/Box'
import { ProgressBarGroup } from '@shared/components/ProgressBarGroup'

interface StepContent {
  title: string
  text: string
}

export interface WalkthroughStep {
  key: string
  banner?: () => React.ReactNode
  background?: CSSProperties['background']
}

interface Props {
  title: string
  steps: WalkthroughStep[]
  productId: TickmillProductType
  dark?: boolean
}

type Direction = 1 | -1

const WALKTHROUGH_PROGRESS_BAR_DURATION = 0.14
const BACKGROUND_URL_PATTERN = /url\(["']?([^"')]+)["']?\)/g

const slideVariants = {
  enter: (direction: Direction) => ({ opacity: 0, x: direction > 0 ? 48 : -48 }),
  center: { opacity: 1, x: 0 },
  exit: (direction: Direction) => ({ opacity: 0, x: direction > 0 ? -48 : 48 }),
}

const getBackgroundImageUrls = (background?: CSSProperties['background']) => {
  if (typeof background !== 'string') return []

  return Array.from(background.matchAll(BACKGROUND_URL_PATTERN), ([, url]) => url)
}

export const WalkthroughModal: BaseModalType<Props> = (props) => {
  const { title, close, steps, dark, productId } = props
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { addProduct } = useTryProduct(productId)
  const [currentStep, setCurrentStep] = useState(0)
  const [previousStep, setPreviousStep] = useState(0)
  const [direction, setDirection] = useState<Direction>(1)
  const [startTestLoading, setStartTestLoading] = useState(false)
  const activeStep = steps[currentStep]
  const Banner = activeStep.banner

  useEffect(() => {
    const imageUrls = Array.from(
      new Set(steps.flatMap((step) => getBackgroundImageUrls(step.background)))
    )
    const preloadLinks = imageUrls.map((url) => {
      const link = document.createElement('link')

      link.rel = 'preload'
      link.as = 'image'
      link.href = url
      document.head.appendChild(link)

      return link
    })

    return () => preloadLinks.forEach((link) => link.remove())
  }, [steps])

  const animatedStepIndexes =
    currentStep > previousStep
      ? Array.from({ length: currentStep - previousStep }, (_, offset) => previousStep + offset + 1)
      : Array.from({ length: previousStep - currentStep }, (_, offset) => previousStep - offset)

  const animatedStepDelays = new Map(
    animatedStepIndexes.map((stepIndex, animationIndex) => [
      stepIndex,
      animationIndex * WALKTHROUGH_PROGRESS_BAR_DURATION * 0.9,
    ])
  )

  const goToStep = (nextStep: number) => {
    if (nextStep === currentStep || nextStep < 0 || nextStep >= steps.length) {
      return
    }

    setDirection(nextStep > currentStep ? 1 : -1)
    setPreviousStep(currentStep)
    setCurrentStep(nextStep)
  }

  const isLastStep = currentStep === steps.length - 1

  const sections = steps.map((_, idx) => ({
    id: `walkthrough-step-${idx}`,
    percentage: currentStep >= idx ? 100 : 0,
    delay: animatedStepDelays.get(idx) ?? 0,
    onClick: () => goToStep(idx),
  }))

  const openProductRegistration = async () => {
    setStartTestLoading(true)
    try {
      await addProduct()
      await close()
      navigate(`/dashboard/product-registration/${productId}/legal-documents`)
    } finally {
      setStartTestLoading(false)
    }
  }

  const handleNext = () => {
    if (isLastStep) return openProductRegistration()
    return goToStep(currentStep + 1)
  }

  const startTest: ButtonProps = {
    children: t('Register'),
    loading: startTestLoading,
    onClick: openProductRegistration,
  }

  return (
    <Modal.FullScreen fullHeight background={activeStep.background}>
      {title && (
        <Modal.Header canClose={false}>
          <Box flex align='center' gap='x24'>
            <ProgressBarGroup
              color='red'
              duration={WALKTHROUGH_PROGRESS_BAR_DURATION}
              size='small'
              stripesVariant='primary'
              sections={sections}
            />
            <IconButton.Tertiary size='sm' onClick={close} icon={CloseIcon} />
          </Box>
        </Modal.Header>
      )}

      <Modal.Body>
        <Box
          fullWidth
          style={{
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <AnimatePresence initial={false} mode='wait' custom={direction}>
            <motion.div
              key={currentStep}
              custom={direction}
              variants={slideVariants}
              initial='enter'
              animate='center'
              exit='exit'
              transition={{
                x: { duration: 0.32, ease: EASE_OUT },
                opacity: { duration: 0.18, ease: 'easeOut' },
              }}
              style={{
                width: '100%',
                willChange: 'transform, opacity',
              }}
            >
              <Box flex direction='column' gap='x24' fullWidth>
                {Banner && <Banner />}

                <Box flex direction='column' gap='x16'>
                  <Modal.Body.Title color={dark ? 'text-white' : 'text-high-em'}>
                    {(t(activeStep.key, { returnObjects: true }) as StepContent).title}
                  </Modal.Body.Title>
                  <Modal.Body.Text color={dark ? 'text-white' : 'text-high-em'}>
                    {(t(activeStep.key, { returnObjects: true }) as StepContent).text}
                  </Modal.Body.Text>
                </Box>
              </Box>
            </motion.div>
          </AnimatePresence>
        </Box>
      </Modal.Body>

      <Modal.Footer>
        {!isLastStep && <Modal.Footer.Secondary {...startTest} />}
        <Modal.Footer.Primary
          loading={isLastStep ? startTest.loading : undefined}
          onClick={isLastStep ? startTest.onClick : handleNext}
        >
          {isLastStep ? startTest.children : t('Next')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal.FullScreen>
  )
}
